<script setup lang="ts">

import type { fetchedUser } from "@/types/fetchedUser"
import { ref, computed } from 'vue'
import UserPicker from '@/components/reusable/userPicker.vue'
import DefaultProfileImageDisplayer from '@/components/reusable/defaultProfileImageDisplayer.vue'

const props = defineProps<{
    users: fetchedUser[]
    showAddButton?: boolean
    addButtonText?: string
    emptyStateText?: string
    allowRemove?: boolean
    showUserNames?: boolean
    imageSize?: 'mini' | 'small' | 'smaller' 
}>()

const emits = defineEmits<{
    userRemoved: [user: fetchedUser]
    userAdded: [user: fetchedUser]
    addClicked: []
}>()

const isAddingUser = ref<boolean>(false)

function removeUser(user: fetchedUser) {
    emits('userRemoved', user)
}

function startUserAdd() {
    isAddingUser.value = true
    emits('addClicked')
}

function handleUserSelected(user: fetchedUser) {
    isAddingUser.value = false
    emits('userAdded', user)
}

function handlePickerCancelled() {
    isAddingUser.value = false
}

const imageClass = computed(() => {
    switch (props.imageSize) {
        case 'small': return 'rounded-image-small'
        case 'smaller': return 'rounded-image-smaller'
        case 'mini': return 'rounded-image-mini'
        default: return 'rounded-image-smaller'
    }
})
</script>

<template>
    <div class="users-display-container">
        <!-- Empty State -->
        <div v-if="users.length === 0" class="no-user flex items-center">
            <p class="information-text text-2xl">
                {{ emptyStateText || "No users assigned." }}
            </p>
        </div>

        <!-- User List -->
        <div v-for="user in users" :key="user.id || user.emailAddress" class="user-item">
            <div class="user-avatar-container">
                <img v-if="user.profileImage"
                    :class="imageClass" 
                    :src="user.profileImage" 
                    referrerpolicy="no-referrer" 
                    :alt="user.userName"
                    class="shadowed-image"
                >
                <default-profile-image-displayer width="4rem" v-else :firstCharacter="user.userName?.charAt(0) || 'U'"></default-profile-image-displayer>
                
                <!-- Remove Button -->
                <button 
                    v-if="allowRemove"
                    @click="removeUser(user)" 
                    class="user-remove" 
                    type="button"
                    :title="`Remove ${user.userName}`"
                >
                    ×
                </button>

                <!-- Hover Tooltip -->
                <div class="user-tooltip">
                    <div class="tooltip-arrow"></div>
                    <span class="tooltip-text">{{ user.userName }}</span>
                </div>
            </div>
            
            <!-- User Name (optional - for side-by-side display) -->
            <span v-if="showUserNames" class="user-name">{{ user.userName }}</span>
        </div>

        <!-- Add Button -->
        <div v-if="showAddButton" class="add-button-container">
            <button @click="startUserAdd" class="ac-minimal-button">
                {{ addButtonText || "Add" }}
            </button>
            
            <!-- User Picker Slot -->
            <slot 
                name="user-picker" 
                :is-adding="isAddingUser"
                :on-user-selected="handleUserSelected"
                :on-cancelled="handlePickerCancelled"
            >
                <!-- Default user picker -->
                <transition
                    appear
                    type="animation"
                    enter-active-class="animate__animated animate__fadeIn"
                    leave-active-class="animate__animated animate__fadeOut"
                >
                    <user-picker
                        v-if="isAddingUser"
                        @user-selected="handleUserSelected"
                        @cancelled="handlePickerCancelled"
                    />
                </transition>
            </slot>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import "../../styles/core/variables.scss";
@import "../../styles/global/buttons.scss";

.users-display-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;
    padding-top: 1rem;
}

.no-user {
    width: 100%;
    opacity: 0.7;
}

.user-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.user-avatar-container {
    position: relative;
    cursor: pointer;
    transition: all .3s;
}

.user-avatar-container:hover {
    transform: scale(1.1);
}

.user-name {
    font-size: 1.2rem;
    color: var(--text-color);
    font-weight: 500;
}

.user-remove {
    display: none;
    opacity: 0;
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    background-color: rgba(0, 0, 0, .85);
    border-radius: 50%;
    font-size: 1.4rem;
    color: white;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: all .3s;
    z-index: 2;
}

.user-avatar-container:hover .user-remove {
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 1;
}

/* Tooltip Styles */
.user-tooltip {
    position: absolute;
    bottom: -3.5rem;
    left: 50%;
    transform: translateX(-50%);
    background-color: var(--box-color);
    border: 1px solid var(--accent-gray-subtle);
    border-radius: 0.5rem;
    padding: 0.5rem 0.8rem;
    box-shadow: var(--main-box-shadow);
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s;
    z-index: 10;
    white-space: nowrap;
    pointer-events: none;
}

.tooltip-arrow {
    position: absolute;
    top: -0.5rem;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 0.5rem solid transparent;
    border-right: 0.5rem solid transparent;
    border-bottom: 0.5rem solid var(--box-color);
}

.tooltip-arrow::before {
    content: '';
    position: absolute;
    top: -0.1rem;
    left: -0.5rem;
    width: 0;
    height: 0;
    border-left: 0.5rem solid transparent;
    border-right: 0.5rem solid transparent;
    border-bottom: 0.5rem solid var(--accent-gray-subtle);
}

.tooltip-text {
    font-size: 1.2rem;
    color: var(--text-color);
    font-weight: 500;
}

.user-avatar-container:hover .user-tooltip {
    opacity: 1;
    visibility: visible;
}

.add-button-container {
    display: flex;
    align-items: center;
    position: relative;
}

</style>
